enum LogLevel {
  All = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Fatal = 5,
  Off = 6,
}

export enum LayoutType {
  Main,
  Chekout,
}

type AwsRum = 'ON' | 'OFF';

type PyramydairUrls = {
  mainPyramydairUrl: string;
  shoppingCart: string;
  signUpUrl: string;
  signInUrl: string;
  recoverPasswordUrl: string;
  orderCreatedUrl: string;
  userAccountUrl: string;
  wishlistUrl: string;
  bugReportUrl: string;
  myAccountUrl: string;
  myOrdersUrl: string;
  myRewardsUrl: string;
  myGiftCardUrl: string;
  bullseyeBucksUrl: string;
  bullseyeBucksAboutUrl: string;
  aboutUsUrl: string;
  contactUsUrl: string;
  faqUrl: string;
  dealersUrl: string;
  airgunsByBrandUrl: string;
  airGunBuilderUrl: string;
  featuredAirgunsUrl: string;
  orderTrackingUrl: string;
  giftCardsUrl: string;
  requestCatalogUrl: string;
  productFinderUrl: string;
  airgunSafetyUrl: string;
  airgunMapUrl: string;
  airgunResourcesUrl: string;
  pyramydairFacebookUrl: string;
  pyramydairInstagramUrl: string;
  pyramydairTwitterUrl: string;
  pyramydairYoutubeUrl: string;
  accreditedBusiness: string;
  bizratesurveysUrl: string;
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
  onlineAccessibilityUrl: string;
  affiliateProgramUrl: string;
  sellerUrl: string;
  careersUrl: string;
  createAccountUrl: string;
  donationConfirmationUrl: string;
  affirmPayOverTimeImageUrl: string;
  subscribeNewsletterUrl: string;
  servicesUrl: string;
};

type EnvironmentConfig = {
  brandCode: string;
  apiClientSideUrl: string;
  apiServerSideUrl: string;
  mainSiteUrl: string;
  webAdminUrl: string;
  buildApiUrl: string;
  logLevel: LogLevel;
  storageKey: string;
  affirmScriptUrl: string;
  affirmPublicApiKey: string;
  listrakMerchantId: string;
  recaptchaSiteKey: string;
  googleAnalyticsPropertyId: string;
  hawkEndPoint: string;
  hawkTrackingUrl: string;
  hawkClientGUID: string;
  awsRum: AwsRum;
  rumAppId: string;
  rumGuestRoleArn: string;
  rumIdentityPoolId: string;
  csvUrl: string;
  releaseId: string;
};

type AppConfig = {
  isDevelopment: boolean;
};

export type Config = EnvironmentConfig & AppConfig & PyramydairUrls;

const mainPyramydairUrl = process.env.NEXT_PUBLIC_MAIN_PYRAMYDAIR_URL || 'https://uatxcartjava.pyramydair.com';
const mainSiteUrl = process.env.NEXT_PUBLIC_MAIN_SITE_URL || mainPyramydairUrl || 'https://www.pyramydair.com';
const recoverPasswordPath = process.env.NEXT_PUBLIC_BRAND_CODE === '2' ? 'forgot-password' : 'recover-your-password';
const privacyPolicyPath = process.env.NEXT_PUBLIC_BRAND_CODE === '2' ? 'privacypolicy.html' : 'privacy-policy';
const myAccountPath = process.env.NEXT_PUBLIC_BRAND_CODE === '2' ? 'login' : 'my-account';
const createAccountPath = process.env.NEXT_PUBLIC_BRAND_CODE === '2' ? 'register' : 'create-account';

const pyramydairUrlsConfig: PyramydairUrls = {
  mainPyramydairUrl: mainPyramydairUrl,
  shoppingCart: `${mainSiteUrl}/shopping-cart`,
  recoverPasswordUrl: `${mainSiteUrl}/${recoverPasswordPath}`,
  signInUrl: `${mainSiteUrl}/login`,
  signUpUrl: `${mainSiteUrl}/${createAccountPath}`,
  userAccountUrl: `${mainSiteUrl}/${myAccountPath}`,
  orderCreatedUrl: `${mainSiteUrl}/order-created`,
  wishlistUrl: `${mainSiteUrl}/wishlist`,
  bugReportUrl: `${mainPyramydairUrl}/five-off`,
  myAccountUrl: `${mainSiteUrl}/my-account`,
  myOrdersUrl: `${mainSiteUrl}/my-account/Orders`,
  myRewardsUrl: `${mainSiteUrl}/my-account/rewards`,
  myGiftCardUrl: `${mainSiteUrl}/my-account/giftcards`,
  bullseyeBucksUrl: `${mainSiteUrl}/my-account/Redeem_Bullseye_Bucks`,
  contactUsUrl: `${mainPyramydairUrl}/contact-us`,
  aboutUsUrl: `${mainPyramydairUrl}/about`,
  faqUrl: `${mainPyramydairUrl}/faq`,
  bullseyeBucksAboutUrl: `${mainPyramydairUrl}/bullseye-bucks-about`,
  airgunsByBrandUrl: `${mainPyramydairUrl}/brands`,
  airGunBuilderUrl: `${mainPyramydairUrl}/air-gun-builder`,
  featuredAirgunsUrl: `${mainPyramydairUrl}/top-airgun-products`,
  orderTrackingUrl: `${mainPyramydairUrl}/order-tracking`,
  giftCardsUrl: `${mainPyramydairUrl}/gift-cards`,
  requestCatalogUrl: `${mainPyramydairUrl}/request-catalog`,
  productFinderUrl: `${mainPyramydairUrl}/guide/finder/`,
  airgunSafetyUrl: `${mainPyramydairUrl}/airgun-safety`,
  airgunMapUrl: `${mainPyramydairUrl}/airgun-map/`,
  airgunResourcesUrl: `${mainPyramydairUrl}/airgun-resources/`,
  privacyPolicyUrl: `${mainPyramydairUrl}/${privacyPolicyPath}`,
  termsOfUseUrl: `${mainPyramydairUrl}/terms-of-use`,
  onlineAccessibilityUrl: `${mainPyramydairUrl}/online-accessibility`,
  affiliateProgramUrl: `${mainPyramydairUrl}/affiliate`,
  sellerUrl: `${mainPyramydairUrl}/seller`,
  careersUrl: `${mainPyramydairUrl}/careers`,
  createAccountUrl: `${mainPyramydairUrl}/${createAccountPath}`,
  donationConfirmationUrl: `${mainPyramydairUrl}/donation-confirmation`,
  subscribeNewsletterUrl: `${mainPyramydairUrl}/signup`,
  dealersUrl: `${mainPyramydairUrl}/dealer`,
  pyramydairFacebookUrl: process.env.NEXT_PUBLIC_FACEBOOK_URL || 'https://www.facebook.com/PyramydAirCom',
  pyramydairInstagramUrl: process.env.NEXT_PUBLIC_INSTAGRAM_URL || 'https://www.instagram.com/pyramydair/',
  pyramydairTwitterUrl: process.env.NEXT_PUBLIC_TWITTER_URL || 'https://twitter.com/pyramydair',
  pyramydairYoutubeUrl:
    process.env.NEXT_PUBLIC_YOUTUBE_URL || 'https://www.youtube.com/user/pyramydair?sub_confirmation=1',
  accreditedBusiness:
    process.env.NEXT_PUBLIC_ACCREDITED_BUSINESS_URL ||
    'https://www.bbb.org/us/oh/cleveland/profile/online-shopping/pyramyd-air-0312-30000579#tabs',
  bizratesurveysUrl:
    process.env.NEXT_PUBLIC_BIZRATESURVEYS_URL || 'https://bizratesurveys.com/reviews/www.pyramydair.com/30290',
  affirmPayOverTimeImageUrl: 'https://cdn-assets.affirm.com/images/banners/120x90.png',
  servicesUrl: `${mainPyramydairUrl}/services`,
};

const appConfig: AppConfig = {
  isDevelopment: process.env.NODE_ENV === 'development',
};

const envConfig: EnvironmentConfig = {
  brandCode: process.env.NEXT_PUBLIC_BRAND_CODE || '0',
  apiClientSideUrl: process.env.NEXT_PUBLIC_API_URL || 'https://uatxcartjava.pyramydair.com/api-marketplace/api/v1',
  apiServerSideUrl:
    process.env.NEXT_PUBLIC_API_SERVER_SIDE_URL || 'https://uatxcartjava.pyramydair.com/api-marketplace/api/v1',
  buildApiUrl:
    process.env.NEXT_PUBLIC_BUILD_API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    'https://uatxcartjava.pyramydair.com/api-marketplace/api/v1',
  webAdminUrl: process.env.NEXT_PUBLIC_WEB_ADMIN_URL || 'https://uatpyr-db01.pyrworkshop.com/kruto/',
  mainSiteUrl: mainSiteUrl,
  logLevel: process.env.NEXT_PUBLIC_LOG_LEVEL ? LogLevel[process.env.NEXT_PUBLIC_LOG_LEVEL] : LogLevel.All,
  storageKey: process.env.NEXT_PUBLIC_STORAGE_KEY || 'pyramydair',
  affirmScriptUrl: process.env.NEXT_PUBLIC_AFFIRM_SCRIPT_URL || 'https://cdn1-sandbox.affirm.com/js/v2/affirm.js',
  affirmPublicApiKey: process.env.NEXT_PUBLIC_AFFIRM_API_KEY || 'ROL1N534INYTUFJ5',
  listrakMerchantId: process.env.NEXT_PUBLIC_LISTRAK_MERCHANT_ID || 'YAdGm5upelss',
  recaptchaSiteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '6LfycgcTAAAAAE1x6viEnSVjkndbbwoO9Ib35SzQ',
  googleAnalyticsPropertyId: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_PROPERTY_ID || 'GTM-NW7LM7N',
  hawkEndPoint: process.env.NEXT_PUBLIC_HAWK_END_POINT || 'https://dev.hawksearch.net/sites/pyramydairv2/',
  hawkTrackingUrl: process.env.NEXT_PUBLIC_HAWK_TRACKING_URL || 'https://tracking-dev.hawksearch.net',
  hawkClientGUID: process.env.NEXT_PUBLIC_HAWK_CLIENT_GUID || 'b3f4f84e899149c08d22e21093a64d1f',
  awsRum: process.env.NEXT_PUBLIC_AWS_RUM ? (process.env.NEXT_PUBLIC_AWS_RUM as AwsRum) : 'OFF',
  rumAppId: process.env.NEXT_PUBLIC_RUM_APP_ID || '',
  rumGuestRoleArn: process.env.NEXT_PUBLIC_RUM_GUEST_ROLE_ARN || '',
  rumIdentityPoolId: process.env.NEXT_PUBLIC_RUM_IDENTITY_POOL_ID || '',
  csvUrl: process.env.NEXT_PUBLIC_CSV_URL || 'https://admin.pyrworkshop.com/app/products_export?_embed=true',
  releaseId: process.env.NEXT_PUBLIC_RELEASE_ID || '1.0.0',
};

const config: Config = Object.assign({}, appConfig, envConfig, pyramydairUrlsConfig);

export default Object.freeze(config);
